<script setup lang="ts">
/**
 * The `claude` account's plan usage as two hairline meters (the five-hour
 * session and the week, each read against its own time), fixed along the top
 * edge of the window above everything, full-screen sheets included; and, under
 * the topbar, a bright banner from CLAUDE_BANNER_HOURS before a "need Claude"
 * entry on any of the reader's teams starts until it ends.
 *
 * Under them, two more for the box itself: CPU busy (over the last second,
 * falling back to the one-minute load average over the core count) and memory
 * in use — fill is what is taken, as above, so the free part is the empty
 * track. The disk has no bar (it barely moves): only a line in the tooltip,
 * and its chart in StatsModal.
 *
 * The five-hour bar is read against the window's TIME too (`fiveHourBar`): a
 * tick where the window has got to, the difference between that and what is
 * used in green (time ahead) or yellow (use ahead), and what was used off the
 * box in orange at its start — red once that alone outruns the time.
 *
 * The weekly bar is the same (`weeklyBar`): tokens as the fill, the week so far
 * as the tick, the difference between them coloured. Its last stretch, on the
 * right, is the hours it would take to spend what is left flat out — red where
 * the week has run into it: wastage.
 *
 * Beside the hours, "+N% ext" while this five-hour window has risen with
 * nothing on the box using Claude (`currentOutside`): the account is being
 * spent somewhere else — claude.ai, the app, another machine.
 *
 * In place of the usage state, red "CPU", "RAM" and "Claudes" while that limit
 * of the Global settings' "Parallel Claudes" holds another Claude back (`room`,
 * read with the box's meters): Under would say "sending" while nothing can start.
 * The state is still in the tooltip.
 * A held CPU or RAM bar goes red too, whatever it reads — the CPU limit is the
 * last minute's, the bar the last second's.
 *
 * A tap on the strip opens StatsModal: every meter's history as a chart.
 *
 * The meters' height is published as `--usage-h` on the root, and the shell and
 * every full-screen overlay start that far down, so nothing sits under them.
 *
 * All are polled (lib/poll.ts: not while hidden, slower while the server is).
 * The runner reports usage every five minutes, so a minute is plenty for it
 * (and the read is shared with the git panel and the stats view); the box's
 * meters are read every five seconds (SYSTEM_POLL_MS); the banner's list is also
 * refetched whenever the board socket says a calendar changed, and on every reconnect.
 */
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { api } from "../lib/api.js";
import { nowMs } from "../lib/clock.js";
import { usePoll } from "../lib/poll.js";
import { USAGE_STATE_LABELS, USAGE_STATE_TITLES, claudeUsage, fiveHourBar, loadClaudeUsage, weeklyBar, type BarSegment } from "../lib/claudeUsage.js";
import { spanLabel, timeLabel } from "../lib/calendar.js";
import { claudeCalendarGen } from "../lib/claudeStatus.js";
import { useBoard } from "../stores/board.js";
import { useViewport } from "../stores/viewport.js";
import ClaudeMark from "./ClaudeMark.vue";
import StatsModal from "./StatsModal.vue";
import { CLAUDE_BANNER_HOURS } from "@shared/availability.js";
import type { ClaudeRoomBlocks } from "@shared/tmux.js";
import { HOUR_MS, type Availability, type SystemStats, type UsageWindow } from "@shared/types.js";
import { SESSION_WEEKLY_PERCENT, USAGE_STALE_MS, currentOutside, usageState, weeklyAhead, weeklyHoursFlatOut } from "@shared/usage.js";

const board = useBoard();
const viewport = useViewport();

const POLL_MS = 60_000;
/** CPU and RAM move from one second to the next, but a reading every few is
 *  enough to watch them by — and each is a request on a box that may be busy. */
const SYSTEM_POLL_MS = 5000;

const usage = claudeUsage;
const needs = ref<Availability[]>([]);
const system = ref<SystemStats | null>(null);
/** Which "Parallel Claudes" limits hold another Claude back; null while none is set. */
const room = ref<ClaudeRoomBlocks | null>(null);
const statsOpen = ref(false);

/** The meters — Claude's and the box's — are a site admin's, on every board
 *  (Personal and non-coding teams too), and nobody else's. The banner is not
 *  a meter and shows to everyone. */
const showMeters = computed(() => !!board.me?.isAdmin);

/** False when the read failed: the poll then waits longer. */
async function loadSystem(): Promise<boolean> {
  try {
    const r = await api.get<{ stats: SystemStats; room?: ClaudeRoomBlocks | null }>("/api/system-stats");
    system.value = r.stats;
    room.value = r.room ?? null;
    return true;
  } catch {
    // Keep the last reading.
    return false;
  }
}

async function loadNeeds(): Promise<boolean> {
  try {
    needs.value = (await api.get<{ entries: Availability[] }>("/api/availability/need-claude")).entries;
    return true;
  } catch {
    // Keep what we had.
    return false;
  }
}

/** The usage (an admin's) and the banner's list. */
async function loadSlow(): Promise<boolean> {
  const [, ok] = await Promise.all([showMeters.value ? loadClaudeUsage() : undefined, loadNeeds()]);
  return ok;
}

const slowPoll = usePoll(loadSlow, POLL_MS);
const systemPoll = usePoll(loadSystem, SYSTEM_POLL_MS, { active: () => showMeters.value });
const strip = ref<HTMLElement | null>(null);
const sizer = new ResizeObserver(() => publishHeight());

function publishHeight(): void {
  document.documentElement.style.setProperty("--usage-h", `${strip.value?.offsetHeight ?? 0}px`);
}

watch(strip, (el, old) => {
  if (old) sizer.unobserve(old);
  if (el) sizer.observe(el);
  publishHeight();
});

onBeforeUnmount(() => {
  sizer.disconnect();
  document.documentElement.style.removeProperty("--usage-h");
});

watch(claudeCalendarGen, () => void loadNeeds());
/** Back from a dropped socket, whatever changed meanwhile is read — not on the
 *  first open, as the poll has just read it. */
let wasOpen = board.status === "open";
watch(
  () => board.status,
  (s) => {
    if (s === "open" && wasOpen) void slowPoll.now();
    if (s === "open") wasOpen = true;
  },
);

// ── the meters ─────────────────────────────────────────────────────────────

const stale = computed(() => !usage.value?.readAt || nowMs.value - usage.value.readAt > USAGE_STALE_MS || !!usage.value.error);

function untilReset(resetsAt: number): string {
  const minutes = Math.ceil((resetsAt - nowMs.value) / 60_000);
  if (minutes <= 0) return "now";
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, "0")}`;
}

const pct = (w: UsageWindow): string => `${w.percent > 0 && w.percent < 1 ? 1 : Math.round(w.percent)}%`;

/** The fewest hours that would use up what is left of the week flat out: fewer left than that is wastage. */
const flatOut = computed(() => weeklyHoursFlatOut(usage.value));
/** Where the plan usage stands (shared/usage.ts): what Under and Wastage wait for. */
const state = computed(() => usageState(usage.value, nowMs.value));
/** Points over the week's pace past which the five-hour bar goes red. */
const WEEK_OVER_RED = 10;

const stateTitle = computed(() => {
  if (state.value === "unknown" || flatOut.value === null) return "";
  const at = usage.value?.weekly?.resetsAt;
  const weekLeft = at ? Math.max(0, Math.floor((at - nowMs.value) / HOUR_MS)) : null;
  return (
    USAGE_STATE_TITLES[state.value] +
    ` · ${flatOut.value}h to spend the rest of the week flat out (a whole 5-hour window ≈ ${SESSION_WEEKLY_PERCENT}% of it)` +
    (weekLeft === null ? "" : `, ${weekLeft}h until it resets`)
  );
});

/** Plan usage this five-hour window that nothing on the box accounts for. */
const outside = computed(() => currentOutside(usage.value, nowMs.value));
const outsideTitle = computed(() => {
  const o = outside.value;
  if (!o) return "";
  const since = o.firstAt === o.lastAt ? `at ${timeLabel(o.lastAt)}` : `between ${timeLabel(o.firstAt)} and ${timeLabel(o.lastAt)}`;
  return (
    `Used elsewhere: the 5-hour window rose ${pct({ percent: o.points, resetsAt: null })} ${since} while nothing on this box used Claude` +
    " — claude.ai, the app or another machine on the same account"
  );
});

type Meter = {
  key: string;
  percent: number;
  tick: number | null;
  /** The weekly bar's last stretch (`weeklyBar`), and how far its tick is into it. */
  reserve: number | null;
  wasted: number;
  kind: "usage" | "system";
  title: string;
  /** A fill of its own, over the kind's. */
  color?: string;
  /** Pulses green/cyan (`.usage-fill.urgent`). */
  urgent?: boolean;
  /** Stretches drawn over the fill (`fiveHourBar`). */
  segments?: BarSegment[];
};

const claudeMeters = computed(() => {
  const u = usage.value;
  if (!u) return [];
  const out: Meter[] = [];
  if (u.fiveHour) {
    const w = u.fiveHour;
    // Coloured by the usage state: pulsing green/cyan in wastage, grey at the
    // limit, green while Under sends, yellow while the window holds it, red
    // while well over the week's pace, else the accent.
    const over = (weeklyAhead(u.weekly, nowMs.value) ?? 0) > WEEK_OVER_RED;
    const [color, note] =
      state.value === "wastage"
        ? [undefined, " · wastage: allowance is going unspent, sending without pacing"]
        : state.value === "atLimit"
          ? ["var(--muted)", " · at limit: nothing can be sent"]
          : state.value === "under"
            ? ["var(--meter-ok)", " · under: sending"]
            : state.value === "held"
              ? ["var(--meter-held)", " · held: past half this window's pace"]
              : over
                ? ["var(--danger)", ` · more than ${WEEK_OVER_RED}% over the week's pace`]
                : ["var(--accent)", ""];
    // Against the window's time as well: a tick where it is, the difference
    // between it and what is used in green or yellow, use elsewhere in front.
    const { time, segments } = fiveHourBar(u, nowMs.value);
    const gone = time === null ? "" : ` · ${Math.round(time)}% of the window gone`;
    out.push({
      key: "five",
      percent: w.percent,
      tick: time,
      reserve: null,
      wasted: 0,
      kind: "usage",
      title: `Claude, 5-hour: ${pct(w)} used${gone}${w.resetsAt ? ` · resets in ${untilReset(w.resetsAt)}` : ""}${note}`,
      color,
      urgent: state.value === "wastage",
      segments,
    });
  }
  if (u.weekly) {
    const w = u.weekly;
    // Tokens against time in one bar, as the five-hour one: the tick is the
    // week so far, and usage past it is spending the allowance too fast. Its
    // last stretch is the fewest hours that would spend what is left flat
    // out: once the week reaches it, that is wastage.
    const { time, segments, reserve, wasted } = weeklyBar(u, nowMs.value);
    const ahead = weeklyAhead(w, nowMs.value) ?? 0;
    const verdict = ahead > 0 ? `${ahead}% over` : ahead < 0 ? `${-ahead}% under` : "on pace";
    const pace = time === null ? "" : ` · ${Math.round(time)}% of the week gone (${verdict})`;
    out.push({
      key: "week",
      percent: w.percent,
      tick: time,
      reserve,
      wasted,
      kind: "usage",
      title: `Claude, weekly: ${pct(w)} used${w.resetsAt ? ` · resets ${spanStart(w.resetsAt)}` : ""}${pace}`,
      segments,
    });
  }
  return out;
});

/**
 * A box meter's colour, by how little is left: green while there is plenty,
 * shading to amber as use reaches USED_AMBER and to red at USED_RED — so a
 * glance says which resource is running out before the bar reaches the end.
 */
const USED_WARN = 60;
const USED_AMBER = 80;
const USED_RED = 95;
function systemColor(used: number): string {
  if (used < USED_WARN) return "var(--meter-ok)";
  const mix = (to: string, from: string, t: number): string =>
    `color-mix(in oklab, var(${to}) ${Math.round(Math.min(1, Math.max(0, t)) * 100)}%, var(${from}))`;
  if (used < USED_AMBER) return mix("--meter-warn", "--meter-ok", (used - USED_WARN) / (USED_AMBER - USED_WARN));
  return mix("--danger", "--meter-warn", (used - USED_AMBER) / (USED_RED - USED_AMBER));
}

const GB = 1024 ** 3;
const gb = (bytes: number): string => `${(bytes / GB).toFixed(bytes < 10 * GB ? 1 : 0)} GB`;
const clamp = (n: number): number => Math.min(100, Math.max(0, n));

/** The disk's line in the strip's tooltip: it has no bar of its own. */
const diskTitle = computed(() => {
  const d = system.value?.disk;
  if (!d || d.totalBytes <= 0) return "";
  return `Disk: ${Math.round((d.usedBytes / d.totalBytes) * 100)}% used · ${gb(d.totalBytes - d.usedBytes)} free of ${gb(d.totalBytes)}`;
});

/** The box's load and memory: fill is what is taken. */
const systemMeters = computed(() => {
  const s = system.value;
  if (!s) return [];
  const out: Meter[] = [];
  const meter = (key: "cpu" | "ram", percent: number, title: string): void => {
    const held = room.value?.[key];
    out.push({
      key,
      percent: clamp(percent),
      tick: null,
      reserve: null,
      wasted: 0,
      kind: "system",
      title: held ? `${title} · holding Claude back: ${held}` : title,
      color: held ? "var(--danger)" : undefined,
    });
  };
  const { load1, cores } = s.load;
  const loadPct = (load1 / Math.max(1, cores)) * 100;
  const loadText = `load ${load1.toFixed(2)} over ${cores} core${cores === 1 ? "" : "s"}`;
  if (s.cpu) meter("cpu", s.cpu.percent, `CPU: ${Math.round(s.cpu.percent)}% busy · ${loadText}`);
  else meter("cpu", loadPct, `CPU ${loadText} (${Math.round(loadPct)}%)`);
  const { availableBytes, totalBytes } = s.memory;
  if (totalBytes > 0) {
    const used = ((totalBytes - availableBytes) / totalBytes) * 100;
    meter("ram", used, `RAM: ${gb(availableBytes)} free of ${gb(totalBytes)} (${Math.round(used)}% used)`);
  }
  return out;
});

// Signing in as an admin should not wait a whole poll for the meters.
watch(showMeters, (on) => {
  if (!on) return;
  void loadClaudeUsage();
  void systemPoll.now();
});

/** The limits holding another Claude back, as the strip's red labels. */
const roomBlocks = computed(() => {
  const r = room.value;
  if (!r) return [];
  const out: { key: string; label: string; title: string }[] = [];
  if (r.claudes) out.push({ key: "claudes", label: "Claudes", title: r.claudes });
  if (r.cpu) out.push({ key: "cpu", label: "CPU", title: r.cpu });
  if (r.ram) out.push({ key: "ram", label: "RAM", title: r.ram });
  return out;
});

const meters = computed(() => (showMeters.value ? [...claudeMeters.value, ...systemMeters.value] : []));

/**
 * Every meter's numbers in one tooltip, on the whole strip: the bars are 3px
 * each, so hovering one is hovering whichever is nearest, and they all answer
 * the same question anyway.
 */
const summary = computed(() => {
  const u = usage.value;
  const lines = claudeMeters.value.map((m) => m.title);
  if (u) {
    if (stateTitle.value) lines.push(stateTitle.value);
    if (outsideTitle.value) lines.push(outsideTitle.value);
    if (u.readAt) lines.push(`Read ${timeLabel(u.readAt)}`);
    if (u.error) lines.push(`Last read failed: ${u.error}`);
  }
  if (showMeters.value && diskTitle.value) lines.push(diskTitle.value);
  lines.push(...systemMeters.value.map((m) => m.title));
  if (room.value?.claudes) lines.push(`Claudes: holding another back: ${room.value.claudes}`);
  return lines.join("\n");
});

const dayTime = new Intl.DateTimeFormat(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" });
const spanStart = (ms: number): string => dayTime.format(ms);

// ── the banner ─────────────────────────────────────────────────────────────

const DISMISSED_KEY = "todos.claudeNeedsDismissed";

/** `id:updatedAt`, so an entry edited after it was dismissed comes back. */
const dismissed = ref<Set<string>>(readDismissed());

function readDismissed(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

const keyOf = (e: Availability): string => `${e.id}:${e.updatedAt}`;

const shown = computed(() =>
  needs.value.filter(
    (e) =>
      e.endMs > nowMs.value &&
      e.startMs - CLAUDE_BANNER_HOURS * HOUR_MS <= nowMs.value &&
      !dismissed.value.has(keyOf(e)),
  ),
);

function dismiss(): void {
  const next = new Set(dismissed.value);
  for (const e of shown.value) next.add(keyOf(e));
  // Drop keys for entries that are no longer listed, so the set does not grow forever.
  const live = new Set(needs.value.map(keyOf));
  dismissed.value = new Set([...next].filter((k) => live.has(k)));
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...dismissed.value]));
  } catch {
    // Private window: dismissed for this tab only.
  }
}

const teamName = (id: string): string => board.teams.find((t) => t.id === id)?.name ?? "";

function when(e: Availability): string {
  return e.startMs <= nowMs.value ? `now, until ${spanStart(e.endMs)}` : spanLabel(e.startMs, e.endMs);
}

const first = computed(() => shown.value[0] ?? null);

function openCalendar(): void {
  const e = first.value;
  if (e && e.teamId !== board.teamId) board.switchTeam(e.teamId);
  viewport.view = "calendar";
}
</script>

<template>
  <Teleport to="body">
  <div
    v-if="meters.length"
    ref="strip"
    class="usage-strip"
    :title="summary"
    role="button"
    tabindex="0"
    aria-label="Usage and server history"
    @click="statsOpen = true"
    @keydown.enter.prevent="statsOpen = true"
    @keydown.space.prevent="statsOpen = true"
  >
    <div class="usage-meters">
      <div v-for="m in meters" :key="m.key" class="usage-meter" :class="{ stale: stale && m.kind !== 'system' }" :aria-label="m.title" role="img">
        <div
          class="usage-fill"
          :class="{ hot: m.kind === 'usage' && !m.color && !m.urgent && m.percent >= 90, urgent: m.urgent }"
          :style="{ width: m.percent + '%', background: m.color ?? (m.kind === 'system' ? systemColor(m.percent) : undefined) }"
        ></div>
        <div
          v-for="g in m.segments"
          :key="g.key"
          class="usage-segment"
          :class="[g.key, { over: g.over }]"
          :style="{ left: g.left + '%', width: g.width + '%' }"
        ></div>
        <div v-if="m.reserve !== null" class="usage-reserve" :style="{ width: m.reserve + '%' }"></div>
        <div v-if="m.reserve !== null && m.wasted > 0" class="usage-overlap" :style="{ left: 100 - m.reserve + '%', width: m.wasted + '%' }"></div>
        <div v-if="m.tick !== null" class="usage-tick" :style="{ left: m.tick + '%' }"></div>
      </div>
    </div>
    <span v-if="outside" class="usage-elsewhere" :aria-label="outsideTitle">+{{ pct({ percent: outside.points, resetsAt: null }) }} ext</span>
    <span v-for="b in roomBlocks" :key="b.key" class="usage-state usage-room-block" :aria-label="`No new Claude: ${b.title}`">{{ b.label }}</span>
    <span v-if="state !== 'unknown' && !roomBlocks.length" class="usage-state" :class="`usage-state-${state}`" :aria-label="stateTitle">{{ USAGE_STATE_LABELS[state] }}</span>
  </div>
  </Teleport>
  <StatsModal v-if="statsOpen" @close="statsOpen = false" />

  <div v-if="first" class="claude-banner" role="status">
    <button type="button" class="claude-banner-body" title="Open the calendar" @click="openCalendar">
      <ClaudeMark :size="13" />
      <strong>Claude needed</strong>
      <span>{{ board.userName(first.userId) }} · {{ when(first) }}</span>
      <span v-if="board.teams.length > 1" class="claude-banner-team">{{ teamName(first.teamId) }}</span>
      <span v-if="first.note" class="claude-banner-note">{{ first.note }}</span>
      <span v-if="shown.length > 1" class="claude-banner-more">+{{ shown.length - 1 }} more</span>
    </button>
    <button type="button" class="claude-banner-close" title="Dismiss" aria-label="Dismiss" @click="dismiss">×</button>
  </div>
</template>
